import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import Network from '@/Network'
import type {
  PriorityContactPayload,
  PriorityExportList
} from '@/Network/Types/Requests/PriorityPaymentsRequests'
import type {
  PriorityPaymentRow,
  PriorityPaymentsResponse
} from '@/Network/Types/Responses/PriorityPaymentsResponses'
import {
  DEFAULT_PRIORITY_FILTERS,
  normalizePriorityFilters,
  priorityFiltersToQuery
} from '@/modules/company/Approvals/PriorityPayments/filters'
import type { PriorityPaymentsFilters } from '@/modules/company/Approvals/PriorityPayments/filters'
import type { PriorityColumnServerFilter } from '@/modules/company/Approvals/PriorityPayments/constants'
import { getApiErrorMessage } from '@/utils/apiError'

/** Mensagem padrão do erro de carga da lista (a tela a usa para não repeti-la). */
export const PRIORITY_LIST_ERROR = 'Não foi possível carregar os pagamentos prioritários'

/**
 * Tamanho de cada página da busca que resolve plantões fora da página (fila de
 * ataque e cópia em lote). É o teto do contrato: `limit` tem `@Max(200)` no BE,
 * que responde 400 acima disso.
 */
export const PRIORITY_ROWS_PAGE_LIMIT = 200

/**
 * Teto TOTAL de linhas dessa busca (5 páginas de 200). Acima disso o que
 * faltar é tratado como pulado, com o aviso que a tela já mostra.
 */
export const PRIORITY_ROWS_LOOKUP_LIMIT = 1000

/**
 * A busca por id também casa nomes que contêm os dígitos; a linha certa é
 * escolhida pelo id entre essas poucas.
 */
const PRIORITY_ROW_BY_ID_LIMIT = 10

export const usePriorityPaymentsStore = defineStore('priorityPayments', () => {
  /** Guard de corrida: só a listagem mais recente escreve estado. */
  let _fetchId = 0

  const filters = ref<PriorityPaymentsFilters>({ ...DEFAULT_PRIORITY_FILTERS })
  const page = ref(1)
  const response = ref<PriorityPaymentsResponse | null>(null)
  const isLoading = ref(false)
  const isRefetching = ref(false)
  const loadError = ref<string | null>(null)
  const selectedIds = ref<number[]>([])
  const isExporting = ref(false)
  /** `serverNow` da última resposta — base do relógio da faixa de prazo. */
  const lastLoadedAt = ref<string | null>(null)

  const rows = computed<PriorityPaymentRow[]>(() => response.value?.data ?? [])
  const hasLoaded = computed(() => response.value !== null)
  const pageCount = computed(() => {
    const current = response.value
    if (!current || current.limit <= 0) return 0
    return Math.ceil(current.count / current.limit)
  })

  /** Recorte sem paginação — o que a exportação e a fila de ataque usam. */
  const scopeQuery = () => {
    const query = priorityFiltersToQuery(filters.value, page.value)
    delete query.page
    delete query.limit
    return query
  }

  /**
   * Página além do fim (colegas trataram plantões desde o último refresh, ou
   * link com `?page=` velho): o BE devolve `data: []` com `count > 0`. Devolve
   * a última página válida nesse caso; `null` quando a página atual serve.
   */
  const lastPageIfBeyondEnd = (data: PriorityPaymentsResponse): number | null => {
    if (data.data.length || data.count <= 0 || data.limit <= 0) return null
    const lastPage = Math.ceil(data.count / data.limit)
    return page.value > lastPage ? lastPage : null
  }

  async function fetchList(options: { silent?: boolean } = {}): Promise<void> {
    const fetchId = ++_fetchId
    if (!options.silent) {
      if (response.value === null) isLoading.value = true
      else isRefetching.value = true
    }

    try {
      const data = await Network.priorityPayments.list(
        priorityFiltersToQuery(filters.value, page.value)
      )
      if (fetchId !== _fetchId) return
      // Volta para a última página válida em vez de mostrar "nenhum plantão"
      // sem paginação; a tela sincroniza a URL ao ver `page` mudar.
      const lastPage = lastPageIfBeyondEnd(data)
      if (lastPage !== null) {
        page.value = lastPage
        return fetchList(options)
      }
      response.value = data
      lastLoadedAt.value = data.serverNow
      loadError.value = null
    } catch (error) {
      if (fetchId !== _fetchId) return
      // As linhas antigas FICAM: num refresh automático de 60 s, apagar a tela
      // por um erro de rede tiraria da pessoa o recorte que ela estava tratando.
      loadError.value = getApiErrorMessage(error, PRIORITY_LIST_ERROR)
    } finally {
      if (fetchId === _fetchId) {
        isLoading.value = false
        isRefetching.value = false
      }
    }
  }

  /** Filtros vindos da URL: aplica sem request (o `onMounted` da página busca). */
  function hydrate(payload: { filters: PriorityPaymentsFilters; page: number }): void {
    filters.value = normalizePriorityFilters({ ...payload.filters })
    page.value = payload.page
  }

  async function setFilters(patch: Partial<PriorityPaymentsFilters>): Promise<void> {
    // Trocar de aba ou de período pode invalidar a linha do relógio (`slaBand`).
    filters.value = normalizePriorityFilters({ ...filters.value, ...patch })
    page.value = 1
    // A seleção era de OUTRO recorte: manter ids invisíveis faria o "Atacar os
    // N selecionados" entrar em plantões que a pessoa não está mais vendo.
    selectedIds.value = []
    await fetchList()
  }

  /** A aba é navegação, não filtro: limpar os filtros mantém a pessoa na aba. */
  async function clearFilters(): Promise<void> {
    filters.value = { ...DEFAULT_PRIORITY_FILTERS, tab: filters.value.tab }
    page.value = 1
    selectedIds.value = []
    await fetchList()
  }

  async function setPage(nextPage: number): Promise<void> {
    page.value = nextPage
    await fetchList()
  }

  const setSelectedIds = (ids: number[]): void => {
    selectedIds.value = [...ids]
  }

  const clearSelection = (): void => {
    selectedIds.value = []
  }

  /**
   * Sem `list`: o recorte da tela. Com `list`: a lista fixa do BE (ex.: o chip
   * "Antecipe pago sem horário" — A2, 25/09). O mesmo `isExporting` trava os dois.
   */
  async function exportXlsx(list?: PriorityExportList): Promise<void> {
    isExporting.value = true
    try {
      await Network.priorityPayments.exportXlsx(list ? { ...scopeQuery(), list } : scopeQuery())
    } finally {
      isExporting.value = false
    }
  }

  async function fetchAttackQueue(ids?: number[]): Promise<number[]> {
    const query = scopeQuery()
    return Network.priorityPayments.attackQueue(ids?.length ? { ...query, ids } : query)
  }

  /**
   * Linhas do recorte, em páginas de `PRIORITY_ROWS_PAGE_LIMIT` até
   * `PRIORITY_ROWS_LOOKUP_LIMIT`: resolve ids da fila de ataque ou da seleção
   * que não estão na página atual. Para quando acha todos os `wantedIds` (se
   * vierem), quando o recorte acaba ou no teto. Erro na 1ª página sobe; numa
   * seguinte, devolve o que já leu (o resto vira pulado). Não toca em
   * `response` — a página que a pessoa vê continua a mesma.
   */
  async function fetchScopeRows(wantedIds: number[] = []): Promise<PriorityPaymentRow[]> {
    const query = scopeQuery()
    const pending = new Set(wantedIds)
    const collected: PriorityPaymentRow[] = []
    const maxPages = Math.ceil(PRIORITY_ROWS_LOOKUP_LIMIT / PRIORITY_ROWS_PAGE_LIMIT)
    for (let nextPage = 1; nextPage <= maxPages; nextPage++) {
      let data: PriorityPaymentsResponse
      try {
        data = await Network.priorityPayments.list({
          ...query,
          page: nextPage,
          limit: PRIORITY_ROWS_PAGE_LIMIT
        })
      } catch (error) {
        if (nextPage === 1) throw error
        break
      }
      collected.push(...data.data)
      for (const row of data.data) pending.delete(row.doctorStationId)
      const isLastPage =
        data.data.length < PRIORITY_ROWS_PAGE_LIMIT || collected.length >= data.count
      if (isLastPage || (wantedIds.length > 0 && pending.size === 0)) break
    }
    return collected
  }

  /**
   * Uma página de UM grupo do recorte (coluna do Kanban): os filtros da tela
   * com o `slaState[]`/`situations[]` do grupo no lugar dos da tela (quem chama
   * já fez a interseção). Não toca em `response` nem na paginação da Lista.
   */
  async function fetchGroupPage(
    group: PriorityColumnServerFilter,
    groupPage: number,
    limit: number
  ): Promise<PriorityPaymentsResponse> {
    const query = { ...priorityFiltersToQuery(filters.value, groupPage), page: groupPage, limit }
    if (group.slaState) query.slaState = [...group.slaState]
    if (group.situations) query.situations = [...group.situations]
    return Network.priorityPayments.list(query)
  }

  /**
   * Estado atual de UM plantão do recorte, mesmo que ele tenha saído do filtro
   * de situação, estado do SLA ou linha do relógio depois de um envio (ex.:
   * "Não enviado" → "Em auditoria"). Busca
   * pelo id (`search` aceita `doctorStationId`) sem os filtros que mudam com o
   * tratamento; `null` quando o servidor não o devolve. Não toca em `response`.
   */
  async function fetchRowById(doctorStationId: number): Promise<PriorityPaymentRow | null> {
    const query = scopeQuery()
    delete query.situations
    delete query.onlyOverdue
    delete query.onlyCritical
    delete query.stalledHours
    // Depois do envio o estado do SLA muda ("Estourado" vira "Atenção" na
    // auditoria) e a linha sairia do filtro de estado ou da linha do relógio.
    delete query.slaState
    delete query.slaBand
    const data = await Network.priorityPayments.list({
      ...query,
      search: String(doctorStationId),
      page: 1,
      limit: PRIORITY_ROW_BY_ID_LIMIT
    })
    return data.data.find((row) => row.doctorStationId === doctorStationId) ?? null
  }

  async function claim(doctorStationId: number): Promise<void> {
    await Network.priorityPayments.claim(doctorStationId)
  }

  async function releaseClaim(doctorStationId: number): Promise<void> {
    await Network.priorityPayments.releaseClaim(doctorStationId)
  }

  async function registerContact(
    doctorStationId: number,
    payload: PriorityContactPayload
  ): Promise<void> {
    await Network.priorityPayments.registerContact(doctorStationId, payload)
  }

  // $reset manual — obrigatório em stores Composition API (regra do repo).
  function $reset(): void {
    _fetchId++
    filters.value = { ...DEFAULT_PRIORITY_FILTERS }
    page.value = 1
    response.value = null
    isLoading.value = false
    isRefetching.value = false
    loadError.value = null
    selectedIds.value = []
    isExporting.value = false
    lastLoadedAt.value = null
  }

  return {
    filters,
    page,
    response,
    isLoading,
    isRefetching,
    loadError,
    selectedIds,
    isExporting,
    lastLoadedAt,
    rows,
    hasLoaded,
    pageCount,
    fetchList,
    hydrate,
    setFilters,
    clearFilters,
    setPage,
    setSelectedIds,
    clearSelection,
    exportXlsx,
    fetchAttackQueue,
    fetchScopeRows,
    fetchGroupPage,
    fetchRowById,
    claim,
    releaseClaim,
    registerContact,
    $reset
  }
})
